import React from "react";
import axios from "axios";
import { FaXTwitter, FaLinkedinIn, FaInstagram, FaWhatsapp } from "react-icons/fa6";

const cols = [
  {
    title: "Product",
    links: [
      { label: "How it works", href: "#how" },
      { label: "Features", href: "#features" },
      { label: "Pricing", href: "#pricing" },
      { label: "FAQ", href: "#faq" },
      { label: "Blog", href: "/blog" },
    ],
  },
  {
    title: "For users",
    links: [
      { label: "Book a move", href: "#waitlist" },
      { label: "Drive & earn", href: "#waitlist" },
      { label: "Verified drivers", href: "#features" },
      { label: "Admin console", href: "#features" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "#" },
      { label: "Pilot program", href: "#waitlist" },
      { label: "Press kit", href: "#" },
      { label: "Contact", href: "mailto:hello@raanah.com" },
    ],
  },
];

export default function Footer() {
  const [driverQr, setDriverQr] = React.useState(null);

  React.useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    axios
      .get(`${base}/api/marketing/qr-code`, { params: { target: "register?role=driver" } })
      .then((r) => setDriverQr(r.data))
      .catch(() => {});
  }, []);

  return (
    <footer
      id="footer"
      data-testid="main-footer"
      className="bg-[#0A0A0A] text-white"
    >
      <div className="px-6 md:px-12 lg:px-24 pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <a
              href="#top"
              data-testid="footer-logo"
              className="flex items-center gap-2 font-display font-black tracking-tighter text-white"
              aria-label="Raanah home"
            >
              <span className="inline-block w-2.5 h-2.5 bg-[#5BB247]" />
              <span className="text-2xl">raanah<span className="text-[#5BB247]">.</span></span>
            </a>
            <p className="mt-5 text-white/60 leading-relaxed max-w-sm">
              House removals, waste clearance &amp; office moves — Luton van
              jobs matched, escrowed and paid out, without the chase.
            </p>

            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: FaXTwitter, label: "Twitter", href: "#" },
                { Icon: FaLinkedinIn, label: "LinkedIn", href: "#" },
                { Icon: FaInstagram, label: "Instagram", href: "#" },
                { Icon: FaWhatsapp, label: "WhatsApp", href: "#" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0A0A] transition-colors"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {cols.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50">
                {col.title}
              </p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-white/80 hover:text-white transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div className="col-span-2 md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50">
              Reach us
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href="mailto:hello@raanah.com"
                  className="text-white/80 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  hello@raanah.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/RaanahDelivery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-white/80 hover:text-white transition-colors"
                  data-testid="footer-facebook"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.24.19 2.24.19v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/></svg>
                  facebook.com/RaanahDelivery
                </a>
              </li>
              <li className="text-white/60">Leeds · Manchester · Birmingham · London</li>
            </ul>
          </div>
        </div>

        {/* v23 — Scan to join as a driver */}
        {driverQr && (
          <div className="mt-14 pt-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-testid="footer-driver-qr-block">
            <div className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5BB247]">For Luton-van owners</p>
              <p className="mt-3 font-display text-2xl md:text-3xl font-black tracking-tighter text-white leading-tight">
                Scan to join as a driver.
              </p>
              <p className="mt-3 text-sm text-white/60 max-w-md">
                Weekly payouts. Escrow-backed jobs. Apply in 5 minutes — no chasing customers ever again.
              </p>
              <a
                href={driverQr.url}
                data-testid="footer-driver-qr-link"
                className="mt-5 inline-flex items-center gap-2 bg-[#5BB247] hover:bg-[#4A9939] text-[#0A0A0A] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-colors"
              >
                Apply now &rarr;
              </a>
            </div>
            <div className="flex md:justify-end">
              <a
                href={driverQr.url}
                aria-label="Open driver application page"
                className="bg-white p-3 inline-block hover:scale-105 transition-transform"
                data-testid="footer-driver-qr-image-link"
              >
                <img
                  src={`data:image/png;base64,${driverQr.png_b64}`}
                  alt="Scan to join Raanah as a driver"
                  width="148"
                  height="148"
                  className="block"
                  data-testid="footer-driver-qr-image"
                />
              </a>
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} Raanah Technologies. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <a href="#" className="hover:text-white transition-colors" data-testid="footer-privacy">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-testid="footer-terms">
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors" data-testid="footer-security">
              Security
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
